/**
 * API helpers — centralized Cloud Functions URL + auth headers.
 */

import { auth } from '../firebase';

export const API_BASE =
  process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-smartforms-ai.cloudfunctions.net';

/** Build a Cloud Functions URL from a path segment (e.g. `/getFormForEmbed`). */
export function functionsUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${normalized}`;
}

export const GET_FORM_FOR_EMBED_URL =
  process.env.REACT_APP_GET_FORM_URL || functionsUrl('/getFormForEmbed');

export const CHAT_API_URL = functionsUrl('/chatMessage');

export async function getAuthHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    } catch {
      // proceed without token — backend will reject protected routes
    }
  }
  return headers;
}

export async function apiGet(path, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const url = `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  return res.json();
}

export async function apiPost(path, body = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let data = null;
    try {
      data = await res.json();
      if (data?.error) msg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    } catch {
      // non-JSON error body — keep the HTTP status message
    }
    const err = new Error(msg);
    err.status = res.status;
    if (data?.code) err.code = data.code;
    if (data?.owned != null) err.owned = data.owned;
    if (data?.limit != null) err.limit = data.limit;
    throw err;
  }
  return res.json();
}

/** Lead action endpoints */
export const leadApi = {
  getFormConversations: (formId, status) =>
    apiGet('/getFormConversations', { formId, ...(status && status !== 'all' ? { status } : {}) }),
  getWorkspaceConversations: (workspaceId, status) =>
    apiGet('/getWorkspaceConversations', { workspaceId, ...(status && status !== 'all' ? { status } : {}) }),
  getWorkspaceSubmissions: (workspaceId, { formId, spamStatus, limit } = {}) =>
    apiGet('/getWorkspaceSubmissions', {
      workspaceId,
      ...(formId ? { formId } : {}),
      ...(spamStatus && spamStatus !== 'all' ? { spamStatus } : {}),
      ...(limit != null ? { limit: String(limit) } : {}),
    }),
  getCallBackList: (workspaceId) => apiGet('/getCallBackList', { workspaceId }),
  getLeadFormData: (conversationId) =>
    apiGet('/getLeadFormData', { conversationId }),
  turnOffAI: (conversationId) => apiPost('/turnOffAIForLead', { conversationId }),
  turnOnAI: (conversationId) => apiPost('/turnOnAIForLead', { conversationId }),
  delegate: (conversationId, assignedTo, source) =>
    apiPost('/delegateLead', { conversationId, assignedTo, ...(source ? { source } : {}) }),
  markContacted: (conversationId, notes) =>
    apiPost('/markLeadContacted', { conversationId, notes }),
  addNote: (conversationId, text) =>
    apiPost('/addNoteToConversation', { conversationId, text }),
  markBooked: (conversationId, notes) =>
    apiPost('/markManuallyBooked', { conversationId, notes }),
  emailToTeam: (conversationId, recipientEmail) =>
    apiPost('/emailLeadToTeam', { conversationId, recipientEmail }),
  sendManualReply: (conversationId, body) =>
    apiPost('/sendManualReply', { conversationId, body }),
  sendManualSms: (conversationId, body) =>
    apiPost('/sendManualSms', { conversationId, body }),
  updateDealStage: (conversationId, stage, dealValue) =>
    apiPost('/updateDealStage', { conversationId, stage, dealValue: dealValue ?? null }),
  releaseFromQuarantine: (submissionId) =>
    apiPost('/releaseFromQuarantine', { submissionId }),
  markSpamAndBlock: (conversationId, { blockDomain } = {}) =>
    apiPost('/markConversationSpamAndBlock', { conversationId, blockDomain: blockDomain === true }),
  unblockConversation: (conversationId) =>
    apiPost('/unblockConversation', { conversationId }),
  approveSuspiciousLead: (conversationId) =>
    apiPost('/approveSuspiciousLead', { conversationId }),
  retryConsentLedger: (workspaceId, conversationId) =>
    apiPost('/retryConsentLedger', { workspaceId, conversationId }),
  getSubscriptionStatus: (userId) =>
    apiGet('/getSubscriptionStatus', { userId }),
};

/** Pooled lead usage for the selected workspace */
export const usageApi = {
  getUsage: (workspaceId) => apiGet('/getUsage', { workspaceId }),
};

/** Client workspace create / archive (owner + cap; flag in functions-forms/src/shared/flags.ts) */
export const workspacesApi = {
  createWorkspace: (name) => apiPost('/createWorkspace', { name }),
  archiveWorkspace: (workspaceId) => apiPost('/archiveWorkspace', { workspaceId }),
  unarchiveWorkspace: (workspaceId) => apiPost('/unarchiveWorkspace', { workspaceId }),
};

/** Billing wallet (SMS/voice credits) */
export const walletApi = {
  getWallet: (workspaceId) => apiGet('/getWallet', { workspaceId }),
  getLedger: (workspaceId) => apiGet('/getWalletLedger', { workspaceId }),
  createTopup: (workspaceId, amountUsd, email) =>
    apiPost('/createWalletTopupSession', { workspaceId, amountUsd, email }),
  setSettings: (workspaceId, settings) =>
    apiPost('/setWalletSettings', { workspaceId, ...settings }),
};

/** Billing subscription (Stripe) */
export const billingApi = {
  getSubscriptionStatus: () => apiGet('/getSubscriptionStatus'),
  cancelSubscription: (payload) => apiPost('/cancelSubscription', payload),
  createPortalSession: (payload) => apiPost('/createCustomerPortalSession', payload),
  createCheckoutSession: (payload) => apiPost('/createCheckoutSession', payload),
};

/** KB assistant — one draft call. Client writes the KB only after approval. */
export function draftKbAssistant(payload) {
  return apiPost('/draftKbAssistant', payload);
}

export const teamApi = {
  getMembers: (workspaceId) => apiGet('/getWorkspaceMembers', { workspaceId }),
  invite: (payload) => apiPost('/inviteTeamMember', payload),
  resendInvite: (payload) => apiPost('/resendInvite', payload),
  removeMember: (payload) => apiPost('/removeTeamMember', payload),
  updatePermissions: (payload) => apiPost('/updateMemberPermissions', payload),
};

export const domainApi = {
  checkEmailUniqueness: (email) => apiGet('/checkEmailUniqueness', { email }),
  add: (payload) => apiPost('/addCustomDomain', payload),
  checkStatus: (fromEmail) => apiGet('/checkDomainStatus', { fromEmail }),
  update: (payload) => apiPost('/updateCustomDomain', payload),
  enableInbound: (payload) => apiPost('/enableInboundForDomain', payload),
  checkMx: (domain) => apiGet('/checkMxRecords', { domain }),
  delete: async (payload) => {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(functionsUrl('/deleteCustomDomain'), {
      method: 'DELETE',
      headers,
      body: JSON.stringify(payload),
    });
    return response.json();
  },
};

/** SMS number provisioning status + self-serve onboarding */
export const smsApi = {
  getSmsStatus: (workspaceId) => apiGet('/getSmsStatus', { workspaceId }),
  getSmsActivity: (workspaceId) => apiGet('/getSmsActivity', { workspaceId }),
  searchNumbers: (workspaceId, areaCode, contains) =>
    apiGet('/searchSmsNumbers', {
      workspaceId,
      ...(areaCode ? { areaCode } : {}),
      ...(contains ? { contains } : {}),
    }),
  previewCampaign: async (workspaceId, formId, businessProfile) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/previewSmsCampaign`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ workspaceId, formId, businessProfile }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, ...data };
  },
  startOnboarding: async (payload) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/startSmsOnboarding`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, ...data };
  },
  retryOnboarding: (payload) => apiPost('/retrySmsOnboarding', payload),
  setQuietHoursOverride: (payload) => apiPost('/setSmsQuietHoursOverride', payload),
  updateVoiceSettings: (payload) => apiPost('/updateVoiceSettings', payload),
  requestMissedCallUpgrade: (workspaceId) =>
    apiPost('/requestMissedCallUpgrade', { workspaceId }),
  sendOwnerTestSms: (workspaceId) => apiPost('/sendOwnerTestSms', { workspaceId }),
  getVoiceRecordingBlob: async (workspaceId, callSid) => {
    const headers = await getAuthHeaders();
    const qs = new URLSearchParams({ workspaceId, callSid }).toString();
    const res = await fetch(`${API_BASE}/getVoiceRecording?${qs}`, { headers });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const data = await res.json();
        if (data?.error) msg = data.error;
      } catch {
        // non-JSON
      }
      throw new Error(msg);
    }
    return URL.createObjectURL(await res.blob());
  },
  updateVoiceWaitlist: (payload) => apiPost('/updateVoiceWaitlist', payload),
  deprovision: (workspaceId, confirmPhoneNumber) =>
    apiPost('/deprovisionSmsNumber', { workspaceId, confirmPhoneNumber }),
};

/** Tracking secret store, pixel test, and conversion log */
export const trackingApi = {
  setFormSecret: (formId, platform, accessToken) =>
    apiPost('/setTrackingSecret', { formId, platform, accessToken }),
  setChatSecret: (configId, platform, accessToken) =>
    apiPost('/setTrackingSecret', { configId, platform, accessToken }),
  /**
   * Workspace-scoped conversion log (Proof page).
   * opts: { before?, platform?, outcome?, stage?, limit? }
   */
  getConversionLog: (workspaceId, opts = {}) =>
    apiGet('/getConversionLog', { workspaceId, ...opts }),
  /**
   * Workspace-scoped lead counts + revenue by marketing channel (Proof page).
   * opts: { since? } — ISO 8601 date filter
   */
  getLeadSources: (workspaceId, opts = {}) =>
    apiGet('/getLeadSources', { workspaceId, ...opts }),
  /**
   * Test a tracking pixel. Pass the raw token in pixelConfig.accessToken so the
   * backend uses that value rather than looking up the stored token — required for
   * guided-flow pre-save validation. testEventCode pins the event to Meta's Test
   * Events panel (default 'TEST12345') so guided-flow tests never land as real
   * conversion data.
   */
  testPixel: (platform, pixelConfig, formId, testEventCode = 'TEST12345') =>
    apiPost('/testTrackingPixel', { platform, pixelConfig, formId, testEventCode }),
};

/** Pre-launch verification - thin wrappers for the three public test endpoints */
export const verifyApi = {
  testSpamSample: (formData, businessContext) =>
    apiPost('/testSpamDetection', { formData, businessContext }),
  testAiReply: (payload) => apiPost('/testAIReplyGeneration', payload),
  testChat: ({ workspaceId, knowledgeBaseId, message }) =>
    apiPost('/chatMessage', { workspaceId, knowledgeBaseId, message }),
};

/** Auth emails (Postmark-branded verification + password reset) */
export const authApi = {
  sendVerificationEmail: () => apiPost('/sendVerificationEmail', {}),
  sendPasswordResetEmail: (email) => apiPost('/sendPasswordResetEmail', { email }),
};

/** Team invite endpoints (accept-invite page) */
export const inviteApi = {
  getDetails: (token) => apiGet('/getInviteDetails', { token }),
  accept: (inviteId) => apiPost('/acceptInvite', { inviteId }),
};

/** Admin-only endpoints */
export const adminApi = {
  getSystemAlerts: (limit = 50, areaPrefix) =>
    apiGet('/getSystemAlerts', {
      limit: String(limit),
      ...(areaPrefix ? { areaPrefix } : {}),
    }),
};

/** Google Ads integration endpoints */
function googleAdsParams(workspaceId) {
  return workspaceId ? { workspaceId } : {};
}

export const googleAdsApi = {
  status: (workspaceId) =>
    apiGet('/getGoogleAdsConnectionStatus', googleAdsParams(workspaceId)),
  startConnect: (workspaceId) =>
    apiPost('/startGoogleAdsConnect', googleAdsParams(workspaceId)),
  listAccounts: (workspaceId) =>
    apiGet('/listGoogleAdsAccounts', googleAdsParams(workspaceId)),
  finishConnect: (customerId, { loginCustomerId, workspaceId } = {}) =>
    apiPost('/finishGoogleAdsConnect', {
      customerId,
      ...(loginCustomerId ? { loginCustomerId } : {}),
      ...googleAdsParams(workspaceId),
    }),
  testConversion: (workspaceId) =>
    apiPost('/testGoogleAdsOfflineConversion', googleAdsParams(workspaceId)),
};

/** Website scrape — used by the onboarding wizard, KB library, and FormBuilder */
export async function scrapeWebsite(url, { fullContent = true, workspaceId } = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(functionsUrl('/scrapeWebsite'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      url,
      fullContent,
      ...(workspaceId ? { workspaceId } : {}),
    }),
  });
  if (!res.ok) {
    let msg = `Scrape failed: ${res.status}`;
    try {
      const data = await res.json();
      if (data?.error) msg = typeof data.error === 'string' ? data.error : msg;
    } catch {
      // non-JSON error body
    }
    throw new Error(msg);
  }
  return res.json();
}

const RECENT_EMAILS_KEY = 'smartforms_lead_email_recipients';

export function getRecentEmailRecipients() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_EMAILS_KEY) || '[]');
  } catch {
    return [];
  }
}

export function saveRecentEmailRecipient(email) {
  const list = getRecentEmailRecipients().filter((e) => e !== email);
  list.unshift(email);
  localStorage.setItem(RECENT_EMAILS_KEY, JSON.stringify(list.slice(0, 10)));
}

/** Logo upload endpoints */
export const logoApi = {
  uploadFormLogo: (formId, imageBase64, mimeType) =>
    apiPost('/uploadFormLogo', { formId, imageBase64, mimeType }),
  uploadWorkspaceLogo: (workspaceId, imageBase64, mimeType) =>
    apiPost('/uploadWorkspaceLogo', { workspaceId, imageBase64, mimeType }),
};
